import { omitKeys, subsetTypeHasKey, toLocalDateString } from "@hex-insights/core";
import { FormState, formStateHasField } from "@hex-insights/forms";
import { mergeFilterInputs, StudentAcademicTermEnrollmentFilterInput, StudentFilterInput } from "../../../Utilities";
import * as StudentFilterFormValues from "./form-values";

export function countActiveStandardFilters<
	K extends keyof StudentFilterFormValues.FilterFormValues = keyof StudentFilterFormValues.FilterFormValues,
>(formValues: Pick<StudentFilterFormValues.FilterFormValues, K>) {
	let numActive = 0;
	if (subsetTypeHasKey(formValues, "statusEQ") && formValues.statusEQ !== null) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "campusIDsIn") && formValues.campusIDsIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "graduationYearIn") && formValues.graduationYearIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "graduationYearEQ") && formValues.graduationYearEQ !== null) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "homeRoomSectionIDIn") && formValues.homeRoomSectionIDIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "attendanceStatusTodayIDIn") && formValues.attendanceStatusTodayIDIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "hasAttendanceToday") && formValues.hasAttendanceToday !== null) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "reEnrollmentStatusEQ") && formValues.reEnrollmentStatusEQ !== null) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "reEnrollmentMealPlan") && formValues.reEnrollmentMealPlan !== null) {
		numActive++;
	}
	if (
		subsetTypeHasKey(formValues, "reEnrollmentContractStatusEQ") &&
		formValues.reEnrollmentContractStatusEQ !== null
	) {
		numActive++;
	}
	return numActive;
}

export function clearStandardFilters<
	K extends keyof StudentFilterFormValues.FilterFormValues = keyof StudentFilterFormValues.FilterFormValues,
>(formState: FormState<Pick<StudentFilterFormValues.FilterFormValues, K>>) {
	if (formStateHasField(formState, "statusEQ")) {
		formState.formClearFunctions.statusEQ();
	}
	if (formStateHasField(formState, "campusIDsIn")) {
		formState.formClearFunctions.campusIDsIn();
	}
	if (formStateHasField(formState, "graduationYearIn")) {
		formState.formClearFunctions.graduationYearIn();
	}
	if (formStateHasField(formState, "graduationYearEQ")) {
		formState.formClearFunctions.graduationYearEQ();
	}
	if (formStateHasField(formState, "homeRoomSectionIDIn")) {
		formState.formClearFunctions.homeRoomSectionIDIn();
	}
	if (formStateHasField(formState, "attendanceStatusTodayIDIn")) {
		formState.formClearFunctions.attendanceStatusTodayIDIn();
	}
	if (formStateHasField(formState, "hasAttendanceToday")) {
		formState.formClearFunctions.hasAttendanceToday();
	}
	if (formStateHasField(formState, "reEnrollmentStatusEQ")) {
		formState.formClearFunctions.reEnrollmentStatusEQ();
	}
	if (formStateHasField(formState, "reEnrollmentMealPlan")) {
		formState.formClearFunctions.reEnrollmentMealPlan();
	}
	if (formStateHasField(formState, "reEnrollmentContractStatusEQ")) {
		formState.formClearFunctions.reEnrollmentContractStatusEQ();
	}
}

export function resetStandardFilters<
	K extends keyof StudentFilterFormValues.FilterFormValues = keyof StudentFilterFormValues.FilterFormValues,
>(
	formState: FormState<Pick<StudentFilterFormValues.FilterFormValues, K>>,
	initial: StudentFilterFormValues.FilterFormValues,
) {
	if (formStateHasField(formState, "statusEQ")) {
		formState.formSetFunctions.statusEQ(initial.statusEQ);
	}
	if (formStateHasField(formState, "campusIDsIn")) {
		formState.formSetFunctions.campusIDsIn(initial.campusIDsIn);
	}
	if (formStateHasField(formState, "graduationYearIn")) {
		formState.formSetFunctions.graduationYearIn(initial.graduationYearIn);
	}
	if (formStateHasField(formState, "graduationYearEQ")) {
		formState.formSetFunctions.graduationYearEQ(initial.graduationYearEQ);
	}
	if (formStateHasField(formState, "homeRoomSectionIDIn")) {
		formState.formSetFunctions.homeRoomSectionIDIn(initial.homeRoomSectionIDIn);
	}
	if (formStateHasField(formState, "attendanceStatusTodayIDIn")) {
		formState.formSetFunctions.attendanceStatusTodayIDIn(initial.homeRoomSectionIDIn);
	}
	if (formStateHasField(formState, "hasAttendanceToday")) {
		formState.formSetFunctions.hasAttendanceToday(initial.hasAttendanceToday);
	}
	if (formStateHasField(formState, "reEnrollmentStatusEQ")) {
		formState.formSetFunctions.reEnrollmentStatusEQ(initial.reEnrollmentStatusEQ);
	}
	if (formStateHasField(formState, "reEnrollmentMealPlan")) {
		formState.formSetFunctions.reEnrollmentMealPlan(initial.reEnrollmentMealPlan);
	}
	if (formStateHasField(formState, "reEnrollmentContractStatusEQ")) {
		formState.formSetFunctions.reEnrollmentContractStatusEQ(initial.reEnrollmentContractStatusEQ);
	}
}

export function prepareFormValuesForConversion<
	K extends keyof StudentFilterFormValues.FilterFormValues = keyof StudentFilterFormValues.FilterFormValues,
>(formValues: Pick<StudentFilterFormValues.FilterFormValues, K>) {
	return omitKeys(formValues, [
		"statusEQ",
		"campusIDsIn",
		"homeRoomSectionIDIn",
		"attendanceStatusTodayIDIn",
		"hasAttendanceToday",
		"reEnrollmentStatusEQ",
		"reEnrollmentMealPlan",
		"reEnrollmentContractStatusEQ",
	]) as Pick<
		StudentFilterFormValues.FilterFormValues,
		Exclude<
			K,
			| "statusEQ"
			| "campusIDsIn"
			| "homeRoomSectionIDIn"
			| "attendanceStatusTodayIDIn"
			| "hasAttendanceToday"
			| "reEnrollmentStatusEQ"
			| "reEnrollmentMealPlan"
			| "reEnrollmentContractStatusEQ"
		>
	>;
}

export function toFilterInputs<
	K extends keyof StudentFilterFormValues.FilterFormValues = keyof StudentFilterFormValues.FilterFormValues,
>(formValues: Pick<StudentFilterFormValues.FilterFormValues, K>, filterInput: StudentFilterInput): StudentFilterInput {
	const customFilterInputs: StudentFilterInput[] = [];

	const today = toLocalDateString(new Date());
	const activeTermFilter = [
		{
			startDateLTE: today,
			endDateGTE: today,
		},
	];

	if (subsetTypeHasKey(formValues, "statusEQ") && formValues.statusEQ !== null) {
		const activeEnrollmentsFilter: StudentAcademicTermEnrollmentFilterInput[] = [
			{
				academicTerm: activeTermFilter,
				and: [
					{
						or: [{ earlyEndDateIsNull: true }, { earlyEndDateGTE: today }],
					},
					{
						or: [{ lateStartDateIsNull: true }, { lateStartDateLTE: today }],
					},
				],
			},
		];
		const futureEnrollmentsFilter: StudentAcademicTermEnrollmentFilterInput[] = [
			{
				or: [
					{
						academicTerm: [
							{
								startDateGT: today,
							},
						],
					},
					{
						academicTerm: activeTermFilter,
						lateStartDateGT: today,
					},
				],
			},
		];
		const formerEnrollmentsFilter: StudentAcademicTermEnrollmentFilterInput[] = [
			{
				or: [
					{
						academicTerm: [
							{
								endDateLT: today,
							},
						],
					},
					{
						academicTerm: activeTermFilter,
						earlyEndDateLT: today,
					},
				],
			},
		];

		switch (formValues.statusEQ) {
			case "Enrolled":
				customFilterInputs.push({ studentAcademicTermEnrollments: activeEnrollmentsFilter });
				break;
			case "Future":
				customFilterInputs.push({
					not: { studentAcademicTermEnrollments: activeEnrollmentsFilter },
					studentAcademicTermEnrollments: futureEnrollmentsFilter,
				});
				break;
			case "Former":
				customFilterInputs.push({
					not: {
						or: [
							{ studentAcademicTermEnrollments: activeEnrollmentsFilter },
							{ studentAcademicTermEnrollments: futureEnrollmentsFilter },
						],
					},
					studentAcademicTermEnrollments: formerEnrollmentsFilter,
				});
				break;
			case "Not Enrolled":
				customFilterInputs.push({
					hasStudentAcademicTermEnrollments: false,
				});
				break;
		}
	}

	if (subsetTypeHasKey(formValues, "campusIDsIn") && formValues.campusIDsIn.length > 0) {
		customFilterInputs.push({
			or: [
				{
					studentCourseSectionEnrollments: [
						{
							courseSection: [
								{
									academicTerm: activeTermFilter,
									courseSectionPeriods: [
										{ room: [{ buildingFloor: [{ building: [{ campusIDIn: formValues.campusIDsIn }] }] }] },
									],
								},
							],
						},
					],
				},
				{
					studentHomeRoomSectionEnrollments: [
						{
							homeRoomSection: [
								{
									academicTerm: activeTermFilter,
									room: [{ buildingFloor: [{ building: [{ campusIDIn: formValues.campusIDsIn }] }] }],
								},
							],
						},
					],
				},
			],
		});
	}

	if (subsetTypeHasKey(formValues, "homeRoomSectionIDIn") && formValues.homeRoomSectionIDIn.length > 0) {
		customFilterInputs.push({
			studentHomeRoomSectionEnrollments: [
				{
					homeRoomSectionIDIn: formValues.homeRoomSectionIDIn,
				},
			],
		});
	}

	if (subsetTypeHasKey(formValues, "attendanceStatusTodayIDIn") && formValues.attendanceStatusTodayIDIn.length > 0) {
		customFilterInputs.push({
			schoolAttendanceRecords: [
				{
					attendanceDateEQ: today,
					attendanceStatusIDIn: formValues.attendanceStatusTodayIDIn,
				},
			],
		});
	}

	if (subsetTypeHasKey(formValues, "hasAttendanceToday") && formValues.hasAttendanceToday !== null) {
		if (formValues.hasAttendanceToday) {
			customFilterInputs.push({
				schoolAttendanceRecords: [
					{
						attendanceDateEQ: today,
					},
				],
			});
		} else {
			customFilterInputs.push({
				not: {
					schoolAttendanceRecords: [
						{
							attendanceDateEQ: today,
						},
					],
				},
			});
		}
	}

	if (subsetTypeHasKey(formValues, "reEnrollmentStatusEQ") && formValues.reEnrollmentStatusEQ !== null) {
		// TODO dynamic academic year
		switch (formValues.reEnrollmentStatusEQ) {
			case "Re-Enrolling":
				customFilterInputs.push({
					studentEnrollmentIntents: [
						{
							academicYear: [{ nameEQ: "2024-2025" }],
							isEnrollingEQ: true,
						},
					],
				});
				break;
			case "Withdrawing":
				customFilterInputs.push({
					studentEnrollmentIntents: [
						{
							academicYear: [{ nameEQ: "2024-2025" }],
							isEnrollingEQ: false,
						},
					],
				});
				break;
			case "No Response":
				customFilterInputs.push({
					not: {
						studentEnrollmentIntents: [
							{
								academicYear: [{ nameEQ: "2024-2025" }],
							},
						],
					},
				});
				break;
		}
	}

	if (subsetTypeHasKey(formValues, "reEnrollmentMealPlan") && formValues.reEnrollmentMealPlan !== null) {
		// TODO dynamic academic year
		if (formValues.reEnrollmentMealPlan) {
			customFilterInputs.push({
				studentEnrollmentIntents: [
					{
						academicYear: [{ nameEQ: "2024-2025" }],
						isEnrollingEQ: true,
						isInterestedInMealPlanEQ: true,
					},
				],
			});
		} else {
			customFilterInputs.push({
				studentEnrollmentIntents: [
					{
						academicYear: [{ nameEQ: "2024-2025" }],
						isEnrollingEQ: true,
						isInterestedInMealPlanEQ: false,
					},
				],
			});
		}
	}

	if (
		subsetTypeHasKey(formValues, "reEnrollmentContractStatusEQ") &&
		formValues.reEnrollmentContractStatusEQ !== null
	) {
		// TODO dynamic academic term
		const academicTermFilter = [
			{
				nameEQ: "Autumn 2024",
			},
		];
		const incompleteFilter: StudentFilterInput = {
			contracts: [
				{
					academicTerms: academicTermFilter,
					hasContractInstallments: false,
				},
			],
		};

		switch (formValues.reEnrollmentContractStatusEQ) {
			case "Incomplete":
				customFilterInputs.push(incompleteFilter);
				break;
			case "Ready for Review":
				customFilterInputs.push({
					contracts: [
						{
							academicTerms: academicTermFilter,
							hasContractInstallments: true,
							isApprovedEQ: false,
						},
					],
					not: incompleteFilter,
				});
				break;
			case "Approved":
				customFilterInputs.push({
					contracts: [
						{
							academicTerms: academicTermFilter,
							isApprovedEQ: true,
							isFinalizedEQ: false,
							signedFileKeyEQ: "",
						},
					],
					not: {
						contracts: [
							{
								academicTerms: academicTermFilter,
								isApprovedEQ: false,
							},
						],
					},
				});
				break;
			case "Signed & Uploaded":
				customFilterInputs.push({
					contracts: [
						{
							academicTerms: academicTermFilter,
							or: [{ isFinalizedEQ: true }, { signedFileKeyNEQ: "" }],
						},
					],
					not: {
						contracts: [
							{
								academicTerms: academicTermFilter,
								isFinalizedEQ: false,
								signedFileKeyEQ: "",
							},
						],
					},
				});
				break;
		}
	}

	return mergeFilterInputs(filterInput, ...customFilterInputs);
}
